import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, KeenIcon } from '@/components';
import { useSnackbar } from 'notistack';
import { ConfirmationDialog } from '@/components/dialogBox';
import { SystemAnnouncementsService } from '@/services/SystemAnnouncementsService';
import { PaginationState } from '@tanstack/react-table';
import { usePermissions } from '@/hooks/usePermissions';
import {
  AnnouncementListItem,
  AnnouncementStatus,
  AnnouncementUrgency,
} from '../../types/system-announcements.types';
import { formatDisplayDateTime } from '@/utils';

type AnnouncementRow = {
  original: AnnouncementListItem;
};

interface SystemAnnouncementsListingsProps {
  onCreateAnnouncement?: () => void;
  defaultStatusFilter?: 'all' | AnnouncementStatus;
  hideStatusFilter?: boolean;
}

const getStatusLabel = (status: AnnouncementStatus): string => {
  switch (status) {
    case AnnouncementStatus.SCHEDULED:
      return 'Scheduled';
    case AnnouncementStatus.ACTIVE:
      return 'Active';
    case AnnouncementStatus.EXPIRED:
      return 'Expired';
    default:
      return String(status);
  }
};

const SystemAnnouncementsListings = ({
  onCreateAnnouncement,
  defaultStatusFilter = 'all',
  hideStatusFilter = false,
}: SystemAnnouncementsListingsProps) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();

  const [statusFilter, setStatusFilter] = useState<'all' | AnnouncementStatus>(
    defaultStatusFilter
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [announcements, setAnnouncements] = useState<AnnouncementListItem[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [totalItems, setTotalItems] = useState<number | undefined>(undefined);
  const [totalPages, setTotalPages] = useState<number | undefined>(undefined);
  const [confirmDeleteState, setConfirmDeleteState] = useState<{
    isOpen: boolean;
    announcementId: string | null;
  }>({ isOpen: false, announcementId: null });

  const fetchAnnouncements = useCallback(async () => {
    try {
      setIsLoading(true);
      setFetchError(null);

      const params: {
        page?: number;
        limit?: number;
        search?: string;
        status?: AnnouncementStatus;
      } = {
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      };

      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response =
        await SystemAnnouncementsService.listAnnouncements(params);
      const data = (response?.data ?? []) as AnnouncementListItem[];
      setAnnouncements(data);
      setTotalItems(
        response.totalItems ??
          response.total ??
          (Array.isArray(data) ? data.length : 0)
      );
      setTotalPages(response.totalPages ?? response.total_pages);
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to load announcements';
      setFetchError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
      setAnnouncements([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    searchQuery,
    statusFilter,
    pagination.pageIndex,
    pagination.pageSize,
    enqueueSnackbar,
  ]);

  useEffect(() => {
    void fetchAnnouncements();
  }, [fetchAnnouncements]);

  const filteredAnnouncements = useMemo(() => announcements, [announcements]);

  const handleOpenAnnouncement = useCallback(
    (
      announcement: AnnouncementListItem | null,
      mode: 'view' | 'edit' | 'create'
    ) => {
      if (mode === 'create') {
        navigate('/system-announcements/create');
      } else if (announcement?.id) {
        if (mode === 'view') {
          navigate(`/system-announcements/view/${announcement.id}`);
        } else {
          navigate(`/system-announcements/edit/${announcement.id}`);
        }
      }
    },
    [navigate]
  );

  const handleDelete = async () => {
    if (!confirmDeleteState.announcementId) return;
    try {
      await SystemAnnouncementsService.deleteAnnouncement(
        confirmDeleteState.announcementId
      );
      enqueueSnackbar('Announcement deleted successfully', {
        variant: 'success',
      });
      setConfirmDeleteState({ isOpen: false, announcementId: null });
      void fetchAnnouncements();
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to delete announcement';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const canCreate = hasPermission('system_announcements_create');
  const canRead = hasPermission('system_announcements_read');
  const canUpdate = hasPermission('system_announcements_update');
  const canDelete = hasPermission('system_announcements_delete');

  const columns = useMemo(
    () => [
      {
        header: 'Title',
        accessorKey: 'title',
        cell: ({ row }: { row: AnnouncementRow }) =>
          canRead ? (
            <button
              type="button"
              onClick={() => handleOpenAnnouncement(row.original, 'view')}
              className="text-sm font-medium text-primary contrast:text-blue-200 hover:text-primary-active focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={`View ${row.original.title}`}
            >
              {row.original.title}
            </button>
          ) : (
            <span className="text-sm text-gray-800">{row.original.title}</span>
          ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        header: 'Urgency',
        accessorKey: 'urgencyLevel',
        cell: ({ row }: { row: AnnouncementRow }) => {
          const value = row.original.urgencyLevel as AnnouncementUrgency;
          let label = '';
          let bgClass = '';
          let textClass = '';
          switch (value) {
            case AnnouncementUrgency.CRITICAL:
              label = 'Critical';
              bgClass = 'bg-red-100 dark:bg-red-800 contrast:bg-red-800';
              textClass =
                'text-red-800 dark:text-red-200 contrast:text-red-200';
              break;
            case AnnouncementUrgency.WARNING:
              label = 'Warning';
              bgClass = 'bg-amber-100 dark:bg-amber-800 contrast:bg-amber-800';
              textClass =
                'text-amber-800 dark:text-amber-200 contrast:text-amber-200';
              break;
            case AnnouncementUrgency.INFO:
            default:
              label = 'Info';
              bgClass = 'bg-blue-100 dark:bg-blue-800 contrast:bg-blue-800';
              textClass =
                'text-blue-800 dark:text-blue-200 contrast:text-blue-200';
          }
          return (
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${bgClass} ${textClass}`}
            >
              {label}
            </span>
          );
        },
        meta: { className: 'min-w-[110px]' },
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }: { row: AnnouncementRow }) => {
          const statusValue = row.original.status as AnnouncementStatus;
          let bgClass = '';
          let textClass = '';
          switch (statusValue) {
            case AnnouncementStatus.SCHEDULED:
              bgClass = 'bg-amber-100 dark:bg-amber-800 contrast:bg-amber-800';
              textClass =
                'text-amber-800 dark:text-amber-200 contrast:text-amber-200';
              break;
            case AnnouncementStatus.ACTIVE:
              bgClass =
                'bg-emerald-100 dark:bg-emerald-800 contrast:bg-emerald-800';
              textClass =
                'text-emerald-800 dark:text-emerald-200 contrast:text-emerald-200';
              break;
            case AnnouncementStatus.EXPIRED:
            default:
              bgClass = 'bg-gray-100';
              textClass = 'text-gray-800';
          }
          return (
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${bgClass} ${textClass}`}
            >
              {getStatusLabel(statusValue)}
            </span>
          );
        },
        meta: { className: 'min-w-[100px]' },
      },
      {
        header: 'Scheduled At',
        accessorKey: 'scheduledAt',
        cell: ({ row }: { row: AnnouncementRow }) => (
          <span className="text-sm text-gray-700 whitespace-nowrap">
            {formatDisplayDateTime(row.original.scheduledAt)}
          </span>
        ),
        meta: { className: 'min-w-[180px]' },
      },
      {
        header: 'End Time',
        accessorKey: 'endTime',
        cell: ({ row }: { row: AnnouncementRow }) => (
          <span className="text-sm text-gray-700 whitespace-nowrap">
            {formatDisplayDateTime(row.original.endTime)}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        header: 'Actions',
        id: 'actions',
        cell: ({ row }: { row: AnnouncementRow }) => (
          <div className="flex items-center gap-2">
            {canRead && (
              <button
                type="button"
                className="btn btn-sm btn-icon btn-light btn-outline"
                onClick={() => handleOpenAnnouncement(row.original, 'view')}
                title="View announcement"
                aria-label={`View ${row.original.title}`}
              >
                <KeenIcon type="filled" icon="eye" className="text-gray-500" />
              </button>
            )}
            {canRead && (
              <button
                type="button"
                className="btn btn-sm btn-icon btn-light btn-outline"
                onClick={() =>
                  navigate(
                    `/system-announcements/recipients/${row.original.id}`
                  )
                }
                title="View recipients"
                aria-label={`View recipients for ${row.original.title}`}
              >
                <KeenIcon
                  type="filled"
                  icon="people"
                  className="text-gray-500"
                />
              </button>
            )}
            {canUpdate &&
              row.original.status === AnnouncementStatus.SCHEDULED && (
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  onClick={() => handleOpenAnnouncement(row.original, 'edit')}
                  title="Edit announcement"
                  aria-label={`Edit ${row.original.title}`}
                >
                  <KeenIcon
                    type="filled"
                    icon="pencil"
                    className="text-gray-500"
                  />
                </button>
              )}
            {canDelete && (
              <button
                type="button"
                className="btn btn-sm btn-icon btn-light btn-outline"
                onClick={() =>
                  setConfirmDeleteState({
                    isOpen: true,
                    announcementId: row.original.id,
                  })
                }
                title="Delete announcement"
                aria-label={`Delete ${row.original.title}`}
              >
                <KeenIcon type="filled" icon="trash" className="text-red-500" />
              </button>
            )}
          </div>
        ),
        meta: { className: 'w-[150px]' },
      },
    ],
    [handleOpenAnnouncement, canRead, canUpdate, canDelete]
  );

  return (
    <div className="announcement-listings card card-grid w-full">
      <div className="card-header flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex-shrink-0">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-announcements"
              aria-label="Search announcements by title"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
                aria-hidden="true"
              />
              <input
                id="search-announcements"
                placeholder="Search announcements"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search announcements by title"
              />
            </label>
          </div>

          {!hideStatusFilter && (
            <select
              className="select select-sm h-8 sm:h-10 text-sm max-w-[160px]"
              value={statusFilter === 'all' ? 'all' : String(statusFilter)}
              onChange={(e) => {
                const val = e.target.value;
                setStatusFilter(
                  val === 'all' ? 'all' : (Number(val) as AnnouncementStatus)
                );
              }}
              aria-label="Filter by status"
              style={{ minWidth: '110px', width: '130px', flexShrink: 0 }}
            >
              <option value="all">All Status</option>
              <option value={AnnouncementStatus.SCHEDULED}>Scheduled</option>
              <option value={AnnouncementStatus.ACTIVE}>Active</option>
              <option value={AnnouncementStatus.EXPIRED}>Expired</option>
            </select>
          )}

          {(searchQuery || statusFilter !== defaultStatusFilter) && (
            <button
              type="button"
              className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10"
              onClick={() => {
                setSearchQuery('');
                setStatusFilter(defaultStatusFilter);
              }}
              aria-label="Clear all filters"
            >
              <KeenIcon
                type="solid"
                icon="arrows-circle"
                className="text-gray-600"
                aria-hidden="true"
              />
              Clear Filters
            </button>
          )}
        </div>

        {onCreateAnnouncement && canCreate && (
          <div className="mt-2 md:mt-0 flex md:justify-end">
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={onCreateAnnouncement}
              aria-label="Add new announcement"
            >
              Add New
            </button>
          </div>
        )}
      </div>

      <div className="card-body">
        <div className="announcement-grid-wrapper">
          <DataGrid
            columns={columns}
            data={filteredAnnouncements}
            rowSelect={false}
            serverSide={true}
            loading={isLoading}
            messages={{
              empty: fetchError ?? 'No announcements found',
            }}
            pagination={{
              page: pagination.pageIndex,
              size: pagination.pageSize,
              more: true,
              totalItems: totalItems ?? 0,
              totalPages,
              setPagination,
              info: '{from} - {to} of {count}',
              sizes: [10, 25, 50, 100],
              sizesLabel: 'Show',
              sizesDescription: 'per page',
            }}
            aria-label="system announcements table"
          />
        </div>
      </div>

      <ConfirmationDialog
        open={confirmDeleteState.isOpen}
        onClose={() =>
          setConfirmDeleteState({ isOpen: false, announcementId: null })
        }
        onConfirm={handleDelete}
        message="Are you sure you want to delete this announcement?"
        cancelButtonText="No"
        confirmButtonText="Yes"
      />
    </div>
  );
};

export { SystemAnnouncementsListings };
